import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  ButtonGroup,
  Card,
  Stack,
  Form,
  FormLayout,
  Frame,
  Badge,
  Layout,
  Modal,
  Page,
  ResourceItem,
  ResourceList,
  TextField,
  Select,
  RangeSlider,
  TextStyle,
  TextContainer,
  Heading,
  Toast,
} from '@shopify/polaris';
import { CirclePlusMinor } from '@shopify/polaris-icons';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import {
  CREATE_SHOP_CAREER_PORTAL,
  UPDATE_SHOP_CAREER_PORTAL,
  CREATE_BENEFIT,
  UPDATE_BENEFIT,
  DELETE_BENEFIT,
} from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultDarkGrayColor, defaultGrayColor, hslaColor, hexColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMugHot,
  faLaptop,
  faDumbbell,
  faGift,
  faClock,
  faBriefcase,
  faLightbulb,
  faHeart,
  faDesktop,
  faShieldAlt,
  faMoneyBill,
  faBullhorn,
  faFlag,
  faGlasses,
  faAward,
  faHeadset,
  faWineGlassAlt,
  faUser,
  faGlobe,
  faGamepad,
  faPlane,
  faBabyCarriage,
  faTooth,
  faBus,
} from '@fortawesome/free-solid-svg-icons';

const benefitIcons = [
  { key: 'fa-mug-hot', icon: faMugHot },
  { key: 'fa-clock', icon: faClock },
  { key: 'fa-laptop', icon: faLaptop },
  { key: 'fa-dumbbell', icon: faDumbbell },
  { key: 'fa-gift', icon: faGift },
  { key: 'fa-bus', icon: faBus },
  { key: 'fa-lightbulb', icon: faLightbulb },
  { key: 'fa-heart', icon: faHeart },
  { key: 'fa-desktop', icon: faDesktop },
  { key: 'fa-shield-alt', icon: faShieldAlt },
  { key: 'fa-money-bill', icon: faMoneyBill },
  { key: 'fa-bullhorn', icon: faBullhorn },
  { key: 'fa-headset', icon: faHeadset },
  { key: 'fa-flag', icon: faFlag },
  { key: 'fa-glasses', icon: faGlasses },
  { key: 'fa-award', icon: faAward },
  { key: 'fa-wine-glass-alt', icon: faWineGlassAlt },
  { key: 'fa-user', icon: faUser },
  { key: 'fa-globe', icon: faGlobe },
  { key: 'fa-gamepad', icon: faGamepad },
  { key: 'fa-plane', icon: faPlane },
  { key: 'fa-briefcase', icon: faBriefcase },
  { key: 'fa-baby-carriage', icon: faBabyCarriage },
  { key: 'fa-tooth', icon: faTooth },
];
const BenefitsSection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);
  const [createBenefit] = useMutation(CREATE_BENEFIT);
  const [updateBenefit] = useMutation(UPDATE_BENEFIT);
  const [deleteBenefit] = useMutation(DELETE_BENEFIT);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;
  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPremiumUser = isSubscribedUser && currentSubscription.subscriptionType === 'Premium';

  const [form, setForm] = useState({
    benefits: [],
  });
  const [careerPortalTime, setCareerPortalTime] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [preview, setPreview] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isBenefitModalOpen, setIsBenefitModalOpen] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);

  const toggleIsBenefitModalOpen = () => setIsBenefitModalOpen(!isBenefitModalOpen);
  const toggleIsConfirmDeleteBenefitModalOpen = () => setIsConfirmDeleteModalOpen(!isConfirmDeleteModalOpen);
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const benefitsSection = propOr({}, 'benefits_section')(styleControls);
    const benefits = propOr([], 'benefits')(careerPortal);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const benefitsSectionEnabled = propOr(false, 'benefitsSection')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);

    setForm((prevForm) => ({
      ...prevForm,
      benefits,
      ...styleControlsFormatted,
      benefitsSection: benefitsSectionEnabled,
      benefitsSectionStyles: {
        ...prevForm.benefitsSection,
        backgroundColor: benefitsSection?.background_color || defaultWhiteColor,
        headingColor: benefitsSection?.heading_color || defaultGrayColor,
        headingWeight: benefitsSection?.heading_weight || '700',
        headingSize: benefitsSection?.heading_size || '26',
        headingFontFamily: benefitsSection?.heading_font_family || 'inherit',
        headingTextCasing: benefitsSection?.heading_text_casing || 'uppercase',
        subheadingColor: benefitsSection?.subheading_color || defaultGrayColor,
        subheadingWeight: benefitsSection?.subheading_weight || '400',
        subheadingSize: benefitsSection?.subheading_size || '20',
        subheadingFontFamily: benefitsSection?.subheading_font_family || 'inherit',
        subheadingTextCasing: benefitsSection?.subheading_text_casing || 'uppercase',
        bodyColor: benefitsSection?.body_color || defaultGrayColor,
        bodyWeight: benefitsSection?.body_weight || '200',
        bodySize: benefitsSection?.body_size || '20',
        bodyFontFamily: benefitsSection?.body_font_family || 'inherit',
        buttonColor: benefitsSection?.button_color || defaultDarkGrayColor,
        topSpacing: benefitsSection?.top_spacing || '80',
        bottomSpacing: benefitsSection?.bottom_spacing || '80',
      },
      benefitsHeading: careerPortal?.benefitsHeading || '',
      benefitsSubheading: careerPortal?.benefitsSubheading || '',
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      benefitsSectionStyles: {
        ...form.benefitsSectionStyles,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setTimeout(() => {
      setIsSaveBarActive(false);
      setShowSuccessBanner(true);
      setIsLoading(false);
    }, 1000);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setConfirmModal(false);
      setPreview(false);
      let promisesArray = [];

      if (!isEmpty(form.benefits)) {
        promisesArray = form.benefits.map(({ isNew, id, headingText, iconKey }) => {
          if (isNew) {
            return createBenefit({
              variables: {
                headingText,
                iconKey,
                shopifyToken,
              },
            });
          }
          return updateBenefit({
            variables: {
              benefitId: id,
              headingText,
              iconKey,
              shopifyToken,
            },
          });
        });
      }

      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const formFiltered = { ...form };

      delete formFiltered.headingText;
      delete formFiltered.iconKey;

      const payload = { variables: { ...careerPortal, ...formFiltered, shopifyToken, benefitsSection: publish } };

      delete payload.variables.styleControls;

      Promise.all([...promisesArray, save(payload)])
        .then(onSuccess)
        .catch(onError);
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Perks & benefits section'
          subtitle={filterPageTimeLabel('benefits_section')}
          titleMetadata={
            <Badge status={form.benefitsSection ? 'success' : 'default'}>
              {form.benefitsSection ? 'Published' : 'Unpublished'}
            </Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/job-listings-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/departments-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                onClick={onSubmit(true)}
                disabled={!isSaveBarActive && form.benefitsSection}
                connectedDisclosure={
                  form.benefitsSection
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Perks & benefits section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Perks can make candidates choose one company over another. Share what you can offer candidates.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <FormLayout>
                    <TextField
                      id='benefitsHeading'
                      label='Title text'
                      value={form.benefitsHeading}
                      name='benefitsHeading'
                      placeholder={'Benefits and Perks'}
                      onChange={updateField('benefitsHeading')}
                    />
                    <TextField
                      id='benefitsSubheading'
                      label='Subtitle text'
                      value={form.benefitsSubheading}
                      name='benefitsSubheading'
                      onChange={updateField('benefitsSubheading')}
                    />
                  </FormLayout>
                  <br />
                  <TextStyle>Perks & benefits display in rows of 3</TextStyle>
                  <ResourceList
                    items={form.benefits}
                    renderItem={(item) => {
                      const { id, headingText, iconKey } = { ...item };
                      const iconItem = benefitIcons.find(({ key }) => key === iconKey);
                      const { icon: benefitIcon } = { ...iconItem };
                      return (
                        <ResourceItem
                          id={id}
                          shortcutActions={[
                            {
                              content: 'Edit',
                              onAction: () => {
                                setSelectedBenefit({
                                  id,
                                  headingText,
                                  iconKey,
                                });
                                setForm({
                                  ...form,
                                  headingText,
                                  iconKey,
                                });
                                toggleIsBenefitModalOpen();
                              },
                            },
                            {
                              content: 'Remove',
                              onAction: () => {
                                toggleIsConfirmDeleteBenefitModalOpen();
                                setSelectedBenefit(item);
                              },
                            },
                          ]}>
                          <div style={{ padding: '10px 5px' }}>
                            <Stack>
                              <FontAwesomeIcon icon={benefitIcon} size={'2x'} color={'#3a3a3a'} />
                              <h3>
                                <TextStyle variation='strong'>{headingText}</TextStyle>
                              </h3>
                            </Stack>
                          </div>
                        </ResourceItem>
                      );
                    }}
                  />
                  <div className={'left-hand-footer'}>
                    <Button onClick={toggleIsBenefitModalOpen}>Add benefit</Button>
                  </div>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.benefitsSectionStyles?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.benefitsSectionStyles?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.benefitsSectionStyles?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.benefitsSectionStyles?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.benefitsSectionStyles?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.benefitsSectionStyles?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.benefitsSectionStyles?.headingSize}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.benefitsSectionStyles?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'headingFontFamily' })}
                        value={form.benefitsSectionStyles?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'headingWeight' })}
                        value={form.benefitsSectionStyles?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'headingTextCasing' })}
                        value={form.benefitsSectionStyles?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='SUBTITLE TEXT'>
                    <ColorPicker
                      colorName='Subtitle color'
                      defaultColor={form.benefitsSectionStyles?.subheadingColor}
                      onSelectColor={updateColor('subheadingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Subtitle font size'
                        value={form.benefitsSectionStyles?.subheadingSize}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'subheadingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.benefitsSectionStyles?.subheadingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'subheadingFontFamily' })}
                        value={form.benefitsSectionStyles?.subheadingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'subheadingWeight' })}
                        value={form.benefitsSectionStyles?.subheadingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Subtitle text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'subheadingTextCasing' })}
                        value={form.benefitsSectionStyles?.subheadingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='PERK/BENEFIT TEXT'>
                    <ColorPicker
                      colorName='Benefit icon color'
                      defaultColor={form.benefitsSectionStyles?.buttonColor}
                      onSelectColor={updateColor('buttonColor')}
                    />
                    <ColorPicker
                      colorName='Benefit text color'
                      defaultColor={form.benefitsSectionStyles?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Benefit text font size'
                        value={form.benefitsSectionStyles?.bodySize}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'bodySize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.benefitsSectionStyles?.bodySize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Benefit text font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'bodyFontFamily' })}
                        value={form.benefitsSectionStyles?.bodyFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Benefit text font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'benefitsSectionStyles', fieldName: 'bodyWeight' })}
                        value={form.benefitsSectionStyles?.bodyWeight}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive}>
                Publish
              </Button>
            </div>
          </Form>
        </Page>
        {toastMarkup}
      </Frame>
      <Modal
        open={isBenefitModalOpen}
        onClose={toggleIsBenefitModalOpen}
        title={selectedBenefit?.id ? 'Update benefit' : 'Add benefit'}
        large
        primaryAction={{
          content: selectedBenefit?.id ? 'Update benefit' : 'Add benefit',
          onAction: () => {
            const updatedBenefit = {
              headingText: form.headingText,
              iconKey: form.iconKey,
              shopifyToken,
            };

            let updatedBenefits = [...form.benefits];
            const newBenefitId = `new-${updatedBenefits.length + 1}-${updatedBenefit.name}`;
            if (!selectedBenefit?.id) {
              updatedBenefits.push({ id: newBenefitId, ...updatedBenefit, isNew: true });
            } else {
              updatedBenefits = updatedBenefits.map((benefit) => {
                if (benefit.id === selectedBenefit?.id)
                  return {
                    ...benefit,
                    ...updatedBenefit,
                  };
                return benefit;
              });
            }

            setForm({
              ...form,
              headingText: '',
              iconKey: '',
              benefits: updatedBenefits,
            });
            toggleIsBenefitModalOpen();
            setSelectedBenefit(null);
          },
          disabled: !form.headingText || !form.iconKey,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                headingText: '',
                iconKey: '',
              });
              setSelectedBenefit(null);
              toggleIsBenefitModalOpen();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <TextField
              id='headingText'
              label='Description'
              value={form.headingText}
              name='headingText'
              onChange={updateField('headingText')}
            />
            <div className={'benefits-modal-grid'}>
              {benefitIcons.map((iconItem) => {
                const { key, icon: benefitIcon } = { ...iconItem };
                return (
                  <div style={{ color: form.iconKey === key ? '#5C6AC4' : '#DFE3E8' }} key={key}>
                    <Button fullWidth size={'large'} monochrome outline onClick={() => updateField('iconKey')(key)}>
                      <FontAwesomeIcon icon={benefitIcon} size={'2x'} color={form.iconKey === key ? '#5C6AC4' : '#637381'} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!preview}
        large
        onClose={togglePreviewModal}
        primaryAction={{
          content: 'Publish',
          disabled: !isSaveBarActive && form.benefitsSection,
          onAction: onSubmit(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: togglePreviewModal,
          },
        ]}
        title={
          <TextContainer>
            <Heading>Preview perks & benefits section</Heading>
            <p style={{ fontSize: '0.85em' }}>
              Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
              section will look on your store
            </p>
          </TextContainer>
        }
        loading={false}>
        <div className={'preview__modal'}>
          <section
            className='benefits_section'
            style={{
              backgroundColor: `${hslaColor(form.benefitsSectionStyles?.backgroundColor, '#fff')}`,
            }}>
            <div className='wrapper'>
              <h2
                className='heading_text'
                style={{
                  color: `${hslaColor(form.benefitsSectionStyles?.headingColor, '#3a3a3a')}`,
                  fontSize: `${form.benefitsSectionStyles?.headingSize || '26'}px`,
                  fontWeight: `${form.benefitsSectionStyles?.headingWeight || '700'}`,
                }}>
                {form.benefitsHeading || 'Benefits and Perks'}
              </h2>
              <h3
                className='subheading_text'
                style={{
                  color: `${hslaColor(form.benefitsSectionStyles?.subheadingColor, '#3a3a3a')}`,
                  fontSize: `${form.benefitsSectionStyles?.subheadingSize || '20'}px`,
                  fontWeight: `${form.benefitsSectionStyles?.subheadingWeight || '400'}`,
                }}>
                {form.benefitsSubheading || ''}
              </h3>
              <div className='benefits_grid'>
                {(form.benefits || []).map((item, index) => {
                  const { headingText, iconKey } = { ...item };
                  const iconItem = benefitIcons.find(({ key }) => key === iconKey);
                  const { icon: benefitIcon } = { ...iconItem };
                  return (
                    <div className='benefit_item' key={`${index}`}>
                      <FontAwesomeIcon
                        icon={benefitIcon}
                        size={'3x'}
                        color={hexColor(form.benefitsSectionStyles?.buttonColor, '#3a3a3a')}
                      />
                      <h3
                        className='benefit_heading'
                        style={{
                          color: `${hslaColor(form.benefitsSectionStyles?.bodyColor, '#3a3a3a')}`,
                          fontSize: `${form.benefitsSectionStyles?.bodySize || '16'}px`,
                          fontWeight: `${form.benefitsSectionStyles?.bodyWeight || '200'}`,
                        }}>
                        {headingText}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      </Modal>
      <Modal
        open={isConfirmDeleteModalOpen}
        onClose={toggleIsConfirmDeleteBenefitModalOpen}
        title='Remove benefit'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            if (selectedBenefit.isNew) {
              setForm({
                ...form,
                benefits: form.benefits.filter(({ id }) => id !== selectedBenefit.id),
              });
              setSelectedBenefit(null);
              toggleIsConfirmDeleteBenefitModalOpen();
            } else {
              setIsLoading(true);
              deleteBenefit({
                variables: {
                  shopifyToken,
                  benefitId: selectedBenefit.id,
                },
              })
                .then(() => {
                  setForm({
                    ...form,
                    benefits: form.benefits.filter(({ id }) => id !== selectedBenefit.id),
                  });
                  toggleIsConfirmDeleteBenefitModalOpen();
                  toggleToast('The benefit was deleted successfully!');
                  setIsLoading(false);
                  setSelectedBenefit(null);
                  refetch();
                })
                .catch(() => {
                  setIsLoading(false);
                  toggleToast('There was a problem with your request');
                });
            }
          },
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleIsConfirmDeleteBenefitModalOpen,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this benefit?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={confirmModal}
        onClose={toggleConfirmModal}
        title={`${form.benefitsSection ? 'Unpublish' : 'Publish'} section`}
        primaryAction={{
          content: `Yes, ${form.benefitsSection ? 'unpublish' : 'publish'} section`,
          onAction: onSubmit(!form.benefitsSection),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleConfirmModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>{`Do you want to ${form.benefitsSection ? 'unpublish' : 'publish'} this section to your career portal page?`}</p>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
};

export default BenefitsSection;
